import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Avatar, Popover } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

// Helper function for contact type colors
const getContactTypeColor = (type) => {
  switch (type) {
    case 'donor':
      return '#015887'; // FITAHO blue
    case 'nonprofit':
      return '#F8971C'; // FITAHO orange
    case 'volunteer':
      return '#4CAF50'; // Green
    case 'other':
      return '#FFACA9'; // Pink for other
    default:
      return '#9E9E9E'; // Gray for unknown
  }
};

const MessageView = ({ 
  conversation, 
  messages, 
  onBack, 
  onSend, 
  donors, 
  nonprofits, 
  isMobile,
  onAddContact,
  searchQuery,
  userName,
  onOpenSettings,
  isLoading
}) => {
  const messageListRef = useRef(null);
  const [message, setMessage] = useState('');
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [showNameError, setShowNameError] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);

  useEffect(() => {
    // Only scroll if we have messages and we're done loading
    if (messageListRef.current && messages.length > 0 && !isLoading) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
      setShowMessages(true);
    } else {
      setShowMessages(false);
    }
  }, [messages, isLoading]);

  const handleSend = (messageText) => {
    // First check for username
    if (!userName?.trim()) {
      setShowNameError(true);
      return;
    }
    
    // Then check for message content
    if (messageText?.trim()) {
      onSend(messageText);
      setMessage('');
    }
  };

  const handleOpenSettings = () => {
    setShowNameError(false);
    onOpenSettings();
  };

  const handleIconButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleSend(message);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend(message);
    }
  };

  const handleEmojiSelect = (emoji) => {
    const cursorPosition = messageInputRef.current?.selectionStart || message.length;
    const newMessage = message.slice(0, cursorPosition) + emoji.native + message.slice(cursorPosition);
    setMessage(newMessage);
    setEmojiAnchorEl(null);
    // Focus back on input after selecting emoji
    setTimeout(() => {
      messageInputRef.current?.focus();
      // Place cursor after the inserted emoji
      messageInputRef.current?.setSelectionRange(
        cursorPosition + emoji.native.length,
        cursorPosition + emoji.native.length
      );
    }, 0);
  };

  const messageInputRef = useRef(null);

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      bgcolor: '#F5F8FA',
      overflow: 'hidden',
    }}>
      {/* Desktop Header - only show if not mobile */}
      {!isMobile && (
        <Box
          sx={{
            p: 2,
            backgroundColor: getContactTypeColor(conversation?.contactType),
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1100,
          }}
        >
          <Avatar 
            src={conversation?.profile_url}
            sx={{ 
              width: 40, 
              height: 40,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
            }}
          >
            {!conversation?.profile_url && (conversation?.contactName?.[0] || '?')}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>
              {conversation?.contactName}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {conversation?.phoneNumber}
            </Typography>
          </Box>
          <IconButton 
            onClick={onAddContact}
            sx={{
              backgroundColor: 'white',
              color: getContactTypeColor(conversation?.contactType),
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            <PersonAddIcon />
          </IconButton>
        </Box>
      )}

      {/* Messages Container */}
      <Box
        ref={messageListRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 2, sm: 3 },
          pb: isMobile ? '100px' : '0px',
          pt: 2,
          position: 'relative',
          height: '100%',
          opacity: showMessages ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out',
          WebkitOverflowScrolling: 'touch',
          '& > *:last-child': {
            marginBottom: isMobile ? '160px' : '99px'
          }
        }}
      >
        {isLoading ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            opacity: 1
          }}>
            <Typography variant="body1" color="text.secondary">
              Loading messages...
            </Typography>
          </Box>
        ) : (
          messages.map((msg, index) => (
            <Box
              key={msg.id || index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.direction === 'outgoing' ? 'flex-end' : 'flex-start',
                mb: 2,
              }}
            >
              {/* Message bubble */}
              <Box
                sx={{
                  maxWidth: '80%',
                  backgroundColor: msg.direction === 'outgoing' ? 'primary.main' : 'white',
                  color: msg.direction === 'outgoing' ? 'white' : 'text.primary',
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                  '&::before': msg.direction === 'outgoing' ? {
                    content: '""',
                    position: 'absolute',
                    right: -8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    borderStyle: 'solid',
                    borderWidth: '8px 0 8px 8px',
                    borderColor: `transparent transparent transparent primary.main`,
                  } : {
                    content: '""',
                    position: 'absolute',
                    left: -8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    borderStyle: 'solid',
                    borderWidth: '8px 8px 8px 0',
                    borderColor: 'transparent white transparent transparent',
                  },
                }}
              >
                {msg.body && (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {msg.body}
                  </Typography>
                )}
                {msg.mediaUrl && msg.mediaUrl.split(',').map((url, i) => (
                  <Box
                    key={i}
                    component="img"
                    src={url.trim()}
                    alt="MMS attachment"
                    onClick={() => window.open(url.trim(), '_blank')}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 300,
                      borderRadius: 1,
                      mt: msg.body ? 1 : 0,
                      cursor: 'pointer',
                      display: 'block',
                    }}
                  />
                ))}
                {!msg.body && !msg.mediaUrl && (
                  <Typography variant="body1" sx={{ fontStyle: 'italic', opacity: 0.5 }}>
                    Empty message
                  </Typography>
                )}
              </Box>
              
              {/* Metadata (username and time) */}
              <Box
                sx={{
                  mt: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'secondary.main',
                  fontSize: '0.75rem',
                }}
              >
                {msg.userName && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontWeight: 500,
                      color: 'secondary.main'
                    }}
                  >
                    {msg.userName}
                  </Typography>
                )}
                <Typography 
                  variant="caption"
                  sx={{ 
                    color: 'secondary.main'
                  }}
                >
                  {(() => {
                    const messageDate = new Date(msg.timestamp);
                    const today = new Date();
                    const isToday = messageDate.toDateString() === today.toDateString();
                    
                    return isToday
                      ? messageDate.toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit'
                        })
                      : messageDate.toLocaleDateString([], {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                  })()}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </Box>

      {/* Mobile Header + Input Box Container */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderTop: '1px solid',
          borderColor: 'divider',
          transform: isKeyboardOpen && isMobile ? 'translateY(-320px)' : 'none',
          transition: 'transform 0.2s ease-out',
          zIndex: 1100,
        }}
      >
        {/* Mobile Header - only show if mobile */}
        {isMobile && (
          <Box
            sx={{
              py: 1.5,
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              backgroundColor: getContactTypeColor(conversation?.contactType),
              color: 'white',
            }}
          >
            <IconButton 
              onClick={onBack} 
              edge="start" 
              sx={{ 
                ml: -1,
                color: 'white',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              <ArrowBackIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
            <Avatar 
              src={conversation?.profile_url}
              sx={{ 
                width: 32, 
                height: 32,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
              }}
            >
              {!conversation?.profile_url && (conversation?.contactName?.[0] || '?')}
            </Avatar>
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>
                {conversation?.contactName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {conversation?.phoneNumber}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Input Box */}
        <Box sx={{ p: 2 }}>
          <TextField
            className="message-input"
            inputRef={messageInputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder={userName?.trim() ? "Message" : "Set your name in settings before sending messages"}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
            }}
            onKeyPress={handleKeyPress}
            variant="outlined"
            disabled={!userName?.trim()}
            error={!userName?.trim()}
            helperText={!userName?.trim() ? (
              <Typography 
                variant="caption" 
                component="span"
                sx={{ color: 'secondary.main' }}
              >
                Name required - {' '}
                <Button
                  onClick={handleOpenSettings}
                  sx={{
                    p: 0,
                    minWidth: 'auto',
                    textTransform: 'none',
                    color: 'secondary.main',
                    fontWeight: 'inherit',
                    fontSize: 'inherit',
                    verticalAlign: 'baseline',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  click here to set your name
                </Button>
              </Typography>
            ) : ""}
            InputProps={{
              sx: {
                backgroundColor: '#F5F8FA',
                borderRadius: 3,
                '& .MuiOutlinedInput-notchedOutline': { 
                  border: !userName?.trim() ? '1px solid' : 'none',
                  borderColor: !userName?.trim() ? 'secondary.main' : 'transparent'
                },
                '& .Mui-error': {
                  color: 'secondary.main'
                },
                '& .Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'secondary.main'
                }
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
                    disabled={!userName?.trim()}
                    sx={{
                      mr: 1,
                      color: message.trim() && userName?.trim() ? 'primary.main' : 'grey.400',
                    }}
                  >
                    <EmojiEmotionsIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleIconButtonClick}
                    onMouseDown={(e) => {
                    }}
                    disabled={!userName?.trim()}
                    sx={{
                      backgroundColor: message.trim() && userName?.trim() ? 'secondary.main' : 'grey.200',
                      color: message.trim() && userName?.trim() ? 'white' : 'grey.400',
                      '&:hover': {
                        backgroundColor: message.trim() && userName?.trim() ? 'secondary.dark' : 'grey.200',
                      },
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Emoji Picker Popover */}
      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={() => setEmojiAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPopover-paper': {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <Picker 
          data={data} 
          onEmojiSelect={handleEmojiSelect}
          theme="light"
          set="native"
        />
      </Popover>

      {/* Name Error Modal */}
      <Dialog
        open={showNameError}
        onClose={() => setShowNameError(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          color: 'warning.dark',
          fontFamily: 'Merriweather, Georgia, serif',
        }}>
          <WarningIcon color="warning" />
          Name Required
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Please set your name in settings before sending messages. This helps others identify who sent each message.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setShowNameError(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleOpenSettings}
            variant="contained"
            color="warning"
          >
            Open Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageView; 